import {
  CompositeDecorator,
  ContentBlock,
  convertFromRaw,
  convertToRaw,
  EditorState,
  Modifier,
  RawDraftContentState,
  RichUtils,
} from 'draft-js';
import { Dispatch, SetStateAction } from 'react';
import { ImagePlugin, ImageBlock } from './plugins/image';
import { LinkPlugin } from './plugins/link';
import { VideoBlock } from './plugins/video';

export const constants = {
  BlockTypes: {
    UNSTYLED: 'unstyled',
    PARAGRAPH: 'unstyled',
    OL: 'ordered-list-item',
    UL: 'unordered-list-item',
    H1: 'header-one',
    H2: 'header-two',
    H3: 'header-three',
    H4: 'header-four',
    H5: 'header-five',
    H6: 'header-six',
    CODE: 'code-block',
    BLOCKQUOTE: 'blockquote',
    PULLQUOTE: 'pullquote',
    ATOMIC: 'atomic',
    BLOCKQUOTE_CAPTION: 'block-quote-caption',
    CAPTION: 'caption',
    IMAGE: 'image',
    VIDEO: 'video',
    BREAK: 'break',
  },
  InlineTypes: {
    BOLD: 'BOLD',
    CODE: 'CODE',
    ITALIC: 'ITALIC',
    STRIKETHROUGH: 'STRIKETHROUGH',
    UNDERLINE: 'UNDERLINE',
  },
  EntityTypes: {
    LINK: 'LINK',
    BOOK: 'BOOK',
    EVENT: 'EVENT',
  },
  Hyperlink: 'hyperlink',
  HANDLED: 'handled',
  NOT_HANDLED: 'not_handled',
};

export const onButtonClick = (
  style: string,
  editorState: EditorState,
  setEditorState: Dispatch<SetStateAction<EditorState>>
) => {
  if (Object.values(constants.BlockTypes).includes(style)) {
    setEditorState(RichUtils.toggleBlockType(editorState, style));
  }

  if (Object.values(constants.InlineTypes).includes(style)) {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  }
};

export const getCurrentBlock = (editorState: EditorState): ContentBlock => {
  const selectionState = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const block: ContentBlock = contentState.getBlockForKey(
    selectionState.getStartKey()
  );
  return block;
};

export const getEntityIfSelected = (
  entityType: string,
  editorState: EditorState
): { entityKey: string; blockKey: string; data: any } => {
  if (!editorState) {
    return null;
  }
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  const currentBlock = getCurrentBlock(editorState);

  if (!currentBlock) {
    return null;
  }

  let entityKey = null;
  let blockKey = null;
  if (
    currentBlock.getType() !== constants.BlockTypes.ATOMIC &&
    selection.isCollapsed() &&
    currentBlock.getLength() > 0 &&
    selection.getAnchorOffset() > 0
  ) {
    entityKey = currentBlock.getEntityAt(selection.getAnchorOffset() - 1);
    blockKey = currentBlock.getKey();
    const entity = entityKey ? content.getEntity(entityKey) : null;
    if (entity && entity.getType() === entityType) {
      return {
        entityKey,
        blockKey,
        data: entity.getData(),
      };
    }
  } else {
    return null;
  }
};

export const createEmptyState = () => {
  const decorators = new CompositeDecorator([LinkPlugin, ImagePlugin]);

  return EditorState.createEmpty(decorators);
};

export const createStateWith = (state: RawDraftContentState): EditorState => {
  try {
    return EditorState.createWithContent(convertFromRaw(state));
  } catch (e) {
    return createEmptyState();
  }
};

export const convertToDraft = (draftContentString: string): EditorState => {
  let asRawContent: RawDraftContentState;
  try {
    asRawContent = JSON.parse(draftContentString);
  } catch (e) {
    // do nothing
  }

  if (!asRawContent) {
    return createEmptyState();
  } else {
    try {
      return EditorState.createWithContent(
        convertFromRaw(asRawContent),
        new CompositeDecorator([LinkPlugin, ImagePlugin])
      );
    } catch (e) {
      return createEmptyState();
    }
  }
};

export const saveDraftContent = (state: EditorState): string => {
  return JSON.stringify(convertToRaw(state.getCurrentContent()));
};

const Media = (props) => {
  const entity = props.contentState?.getEntity(props?.block?.getEntityAt(0));
  const { src, alt } = entity.getData();
  const type = entity.getType();
  if (type === 'image') {
    return <ImageBlock src={src} alt={alt} />;
  } else if (type === 'video') {
    return <VideoBlock src={src} alt={alt} />;
  } else {
    return null;
  }
};

export const mediaBlockRenderer = (block: ContentBlock) => {
  if (block.getType() === 'atomic') {
    return {
      component: Media,
      editable: false,
    };
  } else {
    return null;
  }
};

export const appendText = (text: string, state: EditorState): EditorState => {
  const currentContent = state.getCurrentContent();
  const currentSelection = state.getSelection();
  const newContent = Modifier.replaceText(
    currentContent,
    currentSelection,
    text
  );

  return EditorState.push(state, newContent, 'insert-characters');
};
